import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    Button,
    Card,
    Input,
    List,
    Modal,
    Select,
    Typography,
    Space,
    Row,
    Col,
    Divider,
    Badge,
    Tag,
    Tooltip,
    InputNumber,
} from "antd";
import {
    EditOutlined,
    ExclamationCircleOutlined,
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    UserOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import AddVictime from "../modals/AddVictime";
import AddAyantDroit from "../modals/AddAyantDroit";
import DetailsVictime from "../modals/DetailsVictime";

const { Title, Text, Paragraph } = Typography;

const VictimesTab = ({ sinistre, victimes, setRefetch, editMode, setEditMode }) => {
    const [victimesList, setVictimesList] = useState(victimes || []);
    const [nombreBlessesLegers, setNombreBlessesLegers] = useState(sinistre?.nombreBlesseLeger);
    const [nombreBlessesMoyens, setNombreBlessesMoyens] = useState(sinistre?.nombreBlesseMoyen);
    const [nombreBlessesGraves, setNombreBlessesGraves] = useState(sinistre?.nombreBlesseGrave);
    const [nombreDeces, setNombreDeces] = useState(Number(sinistre?.nombreDeces) || 0);
    const [typeDegats, setTypeDegats] = useState(sinistre?.typeDegats);
    const [showVictimeUpdateAlert, setShowVictimeUpdateAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isVictimeModalVisible, setIsVictimeModalVisible] = useState(false);
    const [isAyantDroitModalVisible, setIsAyantDroitModalVisible] = useState(false);
    const [isDetailsVictimeModalVisible, setIsDetailsVictimeModalVisible] = useState(false);
    const [currentVictime, setCurrentVictime] = useState(null);
    const [motifVictimeUpdate, setMotifVictimeUpdate] = useState("");
    const [isVictimeUpdateModalVisible, setIsVictimeUpdateModalVisible] = useState(false);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);
    const [adversaireOptions, setAdversaireOptions] = useState([]);

    useEffect(() => {
        setNombreBlessesLegers(sinistre?.nombreBlesseLeger);
        setNombreBlessesMoyens(sinistre?.nombreBlesseMoyen);
        setNombreBlessesGraves(sinistre?.nombreBlesseGrave);
        setNombreDeces(Number(sinistre?.nombreDeces) || 0);
        setTypeDegats(sinistre?.typeDegats);
        setAdversaireOptions(sinistre?.victimes?.filter((v) => v.adversaire) || []);
    }, [sinistre]);

    useEffect(() => {
        setVictimesList(victimes || []);
    }, [victimes]);

    const victimesSansAdversaire = useMemo(
        () => victimesList?.filter((victime) => !victime.adversaire || victime.adversaire === null) || [],
        [victimesList]
    );

    const totalVictimesDeclarees =
        Number(nombreBlessesLegers || 0) +
        Number(nombreBlessesMoyens || 0) +
        Number(nombreBlessesGraves || 0) +
        Number(nombreDeces || 0);

    const maxVictimesAtteint = totalVictimesDeclarees > 0 && victimesSansAdversaire.length >= totalVictimesDeclarees;

    const handleNewVictimeOpen = () => {
        if (maxVictimesAtteint) {
            Modal.warning({
                title: "Limite atteinte",
                content: "Le nombre de victimes ajoutées ne peut pas dépasser le nombre déclaré.",
            });
            return;
        }
        setCurrentVictime(null);
        setEditMode(false);
        setIsVictimeModalVisible(true);
    };

    const handleNewAyantDroitOpen = (victime) => {
        setCurrentVictime(victime);
        setIsAyantDroitModalVisible(true);
    };

    const editVictime = (victime) => {
        setCurrentVictime(victime);
        setEditMode(true);
        setIsVictimeModalVisible(true);
    };

    const handleVictimesCountUpdate = (motif) => {
        setLoading(true);
        // Simulation de mise à jour
        const {
            nombreBlesseMoyen,
            nombreBlesseGrave,
            nombreBlesseLeger,
            nombreDeces: nombreDecesInitial,
            typeDegats: typeDegatsInitial,
        } = sinistre || {};

        if (
            nombreBlesseMoyen === Number(nombreBlessesMoyens) &&
            nombreBlesseGrave === Number(nombreBlessesGraves) &&
            nombreBlesseLeger === Number(nombreBlessesLegers) &&
            Number(nombreDecesInitial || 0) === Number(nombreDeces || 0) &&
            typeDegatsInitial === typeDegats
        ) {
            toast("Aucun changement detecté!");
            setLoading(false);
            return;
        }

        const payload = {
            ...sinistre,
            nombreBlesseMoyen: Number(nombreBlessesMoyens),
            nombreBlesseGrave: Number(nombreBlessesGraves),
            nombreBlesseLeger: Number(nombreBlessesLegers),
            nombreDeces: Number(nombreDeces),
            typeDegats: typeDegats || sinistre?.typeDegats,
        };

        if (motif) {
            payload.motifModification = motif;
        }

        axiosInstance
            .put(`/api/sinistre/victimes-count`, JSON.stringify(payload))
            .then((res) => {
                const data = res.data;
                setNombreBlessesMoyens(data.nombreBlesseMoyen);
                setNombreBlessesGraves(data.nombreBlesseGrave);
                setNombreBlessesLegers(data.nombreBlesseLeger);
                setNombreDeces(data.nombreDeces);
                setTypeDegats(data.typeDegats);
                toast("Nombre de victimes mis à jour!");
                setRefetch && setRefetch((prev) => !prev);
                setShowVictimeUpdateAlert(false);
                setIsVictimeUpdateModalVisible(false);
            })
            .catch(() => {
                toast.error("Impossible de mettre à jour le nombre de victimes");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleVictimeUpdateModalOk = () => {
        handleVictimesCountUpdate(motifVictimeUpdate);
    };

    const getSeverityColor = (type) => {
        const colors = {
            leger: "#52c41a",
            moyen: "#faad14",
            grave: "#ff4d4f",
            deces: "#000000",
        };
        return colors[type] || "#d9d9d9";
    };

    const isDossierClos = sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T";

    const removeVictimeFromList = (victimeId) => {
        setVictimesList((previous) =>
            (previous || []).filter(
                (victime) => (victime?.codeId || victime?.id) !== victimeId
            )
        );
    };

    const deleteVictime = (victimeId) => {
        setDeleteLoadingId(victimeId);
        return axiosInstance
            .delete(`/api/victime/${victimeId}`)
            .then(() => {
                toast.success("Victime supprimée avec succès");
                removeVictimeFromList(victimeId);
                if (currentVictime && (currentVictime.codeId === victimeId || currentVictime.id === victimeId)) {
                    setCurrentVictime(null);
                    setIsDetailsVictimeModalVisible(false);
                }
                setRefetch && setRefetch((prev) => !prev);
            })
            .catch(() => {
                toast.error("Impossible de supprimer la victime");
            })
            .finally(() => {
                setDeleteLoadingId(null);
            });
    };

    const confirmDeleteVictime = (victime) => {
        const victimeId = victime?.codeId || victime?.id;
        if (!victimeId) {
            toast.error("Identifiant de la victime introuvable");
            return;
        }

        Modal.confirm({
            title: "Supprimer cette victime ?",
            icon: <ExclamationCircleOutlined />,
            content: "Cette action supprimera définitivement la victime du dossier.",
            okText: "Supprimer",
            cancelText: "Annuler",
            okButtonProps: {
                danger: true,
                style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" },
            },
            onOk: () => deleteVictime(victimeId),
        });
    };

    return (
        <div style={{ padding: "24px", background: "#f5f7fa", minHeight: "100vh" }}>
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                {/* En-tête */}
                <div style={{
                    background: '#242166',
                    padding: "16px 32px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }}>
                    <Title level={2} style={{ color: "white", margin: 0 }}>
                        <UserOutlined style={{ marginRight: "12px" }} />
                        Gestion des Victimes
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "16px" }}>
                        Sinistre N°: {sinistre?.numSinistre || sinistre?.numeroSinistreM || "N/A"} - Type: {sinistre?.typeSinistre || "N/A"}
                    </Text>
                </div>

                {/* Formulaire de déclaration */}
                <Card
                    title={
                        <Space>
                            <InfoCircleOutlined />
                            <span>Déclaration des victimes</span>
                        </Space>
                    }
                    style={{ marginBottom: "20px", borderRadius: "8px" }}
                >
                    {sinistre?.typeSinistre === "MATERIEL" ? (
                        <Row gutter={16}>
                            <Col span={12}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Type de dégâts</Text>
                                </div>
                                <Select
                                    value={typeDegats}
                                    onChange={setTypeDegats}
                                    style={{ width: "100%" }}
                                    size="large"
                                    placeholder="Sélectionner le type de dégâts"
                                    options={[
                                        { value: "Dégats légers ou sans précision", label: "Dégats légers ou sans précision" },
                                        { value: "Dégâts moyens", label: "Dégâts moyens" },
                                        { value: "Dégâts importants", label: "Dégâts importants" },
                                        { value: "Cas extrêmes", label: "Cas extrêmes" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    ) : (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés légers</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesLegers}
                                    onChange={(e) => setNombreBlessesLegers(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés moyens</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesMoyens}
                                    onChange={(e) => setNombreBlessesMoyens(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés graves</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesGraves}
                                    onChange={(e) => setNombreBlessesGraves(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Décès</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreDeces}
                                    onChange={(e) => setNombreDeces(Number(e))}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            {sinistre?.typeSinistre === "MIXTE" && (
                                <Col xs={24} sm={12} lg={6}>
                                    <div style={{ marginBottom: "8px" }}>
                                        <Text strong>Type de dégâts</Text>
                                    </div>
                                    <Select
                                        value={typeDegats}
                                        onChange={setTypeDegats}
                                        style={{ width: "100%" }}
                                        size="large"
                                        placeholder="Type de dégâts"
                                        options={[
                                            { value: "Dégats légers ou sans précision", label: "Dégats légers" },
                                            { value: "Dégâts moyens", label: "Dégâts moyens" },
                                            { value: "Dégâts importants", label: "Dégâts importants" },
                                            { value: "Cas extrêmes", label: "Cas extrêmes" },
                                        ]}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}

                    <Divider />

                    {showVictimeUpdateAlert && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Mise à jour requise"
                            description="Vous venez d'ajouter une victime, veuillez mettre à jour les champs correspondants."
                            style={{ marginBottom: 16 }}
                            closable
                            onClose={() => setShowVictimeUpdateAlert(false)}
                        />
                    )}

                    <Space>
                        <Button
                            type="primary"
                            style={{ background: '#242166' }}
                            icon={<SaveOutlined />}
                            loading={loading}
                            size="large"
                            disabled={isDossierClos}
                            onClick={() => setIsVictimeUpdateModalVisible(true)}
                        >
                            Mettre à jour
                        </Button>
                        {isDossierClos && (
                            <Tag color="red">Dossier clôturé</Tag>
                        )}
                    </Space>
                </Card>

                {/* Liste des victimes */}
                <Card
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Space>
                                <UserOutlined />
                                <span>Victimes enregistrées</span>
                                <Badge count={victimesList?.length || 0} showZero style={{ backgroundColor: "#52c41a" }} />
                            </Space>
                            <Button
                                type="primary"
                                style={{ background: '#242166' }}
                                icon={<PlusOutlined />}
                                size="large"
                                onClick={handleNewVictimeOpen}
                                disabled={isDossierClos || maxVictimesAtteint}
                            >
                                Nouvelle victime
                            </Button>
                        </div>
                    }
                    style={{ borderRadius: "8px" }}
                >
                    {maxVictimesAtteint && (
                        <Alert
                            type="info"
                            showIcon
                            message="Limite atteinte"
                            description={`Vous avez atteint le nombre maximum de victimes (${totalVictimesDeclarees}). Veuillez augmenter le nombre déclaré pour en ajouter davantage.`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {victimesList?.length > 0 ? (
                        <List
                            grid={{
                                gutter: 16,
                                xs: 1,
                                sm: 1,
                                md: 2,
                                lg: 2,
                                xl: 2,
                                xxl: 3,
                            }}
                            dataSource={victimesList}
                            renderItem={(item, index) => (
                                <List.Item>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: "8px",
                                            border: "1px solid #e8e8e8",
                                            transition: "all 0.3s",
                                            backgroundColor: "ghostwhite",
                                        }}
                                        styles={{
                                            body: { padding: "20px" }
                                        }}
                                        onClick={() => {
                                            setCurrentVictime(item);
                                            setIsDetailsVictimeModalVisible(true);
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                                            <Space>
                                                <Avatar
                                                    size={48}
                                                    src={`https://ui-avatars.com/api/?name=${item?.personne?.prenom
                                                        ? `${item?.personne?.nom}+${item?.personne?.prenom}`
                                                        : item?.personne?.nom || "V"
                                                        }&background=667eea&color=fff`}
                                                />
                                                <div>
                                                    <Text strong style={{ fontSize: "16px" }}>
                                                        {item?.personne?.prenom
                                                            ? `${item?.personne?.nom} ${item?.personne?.prenom}`
                                                            : item?.personne?.nom || "Victime"}
                                                    </Text>
                                                    <br />
                                                    <Tag color={item?.blesseSinistre ? "orange" : "red"}>
                                                        {item?.blesseSinistre ? "Blessé(e)" : item?.decesSinistre ? "Décédé(e)" : ""}
                                                    </Tag>
                                                </div>
                                            </Space>
                                            <Tooltip title="Modifier">
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        editVictime(item);
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Supprimer">
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    loading={deleteLoadingId === (item?.codeId || item?.id)}
                                                    disabled={deleteLoadingId === (item?.codeId || item?.id)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirmDeleteVictime(item);
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>

                                        <Divider style={{ margin: "12px 0" }} />

                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <div>
                                                <Text type="secondary">Profession</Text>
                                                <br />
                                                <Text>{item?.personne?.profession || "Non renseignée"}</Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Adresse</Text>
                                                <br />
                                                <Text ellipsis>{item?.personne?.adresse || "Non renseignée"}</Text>
                                            </div>
                                            {item?.descriptionDegatsVictime && (
                                                <div>
                                                    <Text type="secondary">Dommages</Text>
                                                    <br />
                                                    <Paragraph
                                                        ellipsis={{ rows: 2, expandable: false }}
                                                        style={{ margin: 0 }}
                                                    >
                                                        {item?.descriptionDegatsVictime}
                                                    </Paragraph>
                                                </div>
                                            )}
                                        </Space>

                                        {item?.decesSinistre && (
                                            <>
                                                <Divider style={{ margin: "12px 0" }} />
                                                <Button
                                                    type="dashed"
                                                    block
                                                    icon={<PlusOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNewAyantDroitOpen(item);
                                                    }}
                                                >
                                                    Ajouter un ayant-droit
                                                </Button>
                                            </>
                                        )}
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "60px 0",
                            color: "#999"
                        }}>
                            <UserOutlined style={{ fontSize: "64px", marginBottom: "16px" }} />
                            <br />
                            <Text style={{ fontSize: "16px" }}>
                                Aucune victime enregistrée sur ce dossier sinistre
                            </Text>
                        </div>
                    )}
                </Card>

                {/* Modal de confirmation */}
                <Modal
                    title="Confirmation de mise à jour"
                    open={isVictimeUpdateModalVisible}
                    onOk={handleVictimeUpdateModalOk}
                    onCancel={() => {
                        setIsVictimeUpdateModalVisible(false);
                        setMotifVictimeUpdate("");
                    }}
                    okText="Confirmer"
                    cancelText="Annuler"
                    okButtonProps={{ disabled: !motifVictimeUpdate.trim(), style: { "backgroundColor": "#242166" } }}
                >
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text>Voulez-vous vraiment effectuer cette mise à jour ?</Text>
                        <Input.TextArea
                            rows={4}
                            placeholder="Veuillez indiquer le motif de cette modification..."
                            value={motifVictimeUpdate}
                            onChange={(e) => setMotifVictimeUpdate(e.target.value)}
                            style={{ marginTop: "12px" }}
                        />
                    </Space>
                </Modal>
                <AddVictime
                    open={isVictimeModalVisible}
                    modalExpert={isVictimeModalVisible}
                    victime={currentVictime}
                    setOpen={setIsVictimeModalVisible}
                    sinistre={sinistre}
                    edit={editMode}
                    onSaved={({ action }) => {
                        setRefetch && setRefetch((previous) => !previous);
                        setEditMode(false);
                        if (action === "create") {
                            setShowVictimeUpdateAlert(true);
                        }
                    }}
                    adversaires={adversaireOptions || []}
                />

                <AddAyantDroit
                    open={isAyantDroitModalVisible}
                    modalExpert={isAyantDroitModalVisible}
                    victime={currentVictime}
                    setOpen={setIsAyantDroitModalVisible}
                    ayantDroit={null}
                    edit={editMode}
                />

                <DetailsVictime
                    open={isDetailsVictimeModalVisible}
                    victime={currentVictime}
                    setVictime={setCurrentVictime}
                    setOpen={setIsDetailsVictimeModalVisible}
                />
            </div>
        </div>
    );
};

export default VictimesTab;
